(() => {
  if (window.OTTUNIGlobalShell) return;

  const instances = new Map();
  const desktopMedia = window.matchMedia('(min-width: 1241px)');
  let scrollFrame = 0;

  const updateHeaderOffset = () => {
    const heights = Array.from(instances.keys()).map((root) => root.getBoundingClientRect().height);
    const height = heights.length ? Math.max(...heights) : 0;

    if (height > 0) {
      const offset = `${Math.ceil(height)}px`;
      document.documentElement.style.setProperty('--header-group-height', offset);
      document.body.style.setProperty('--header-group-height', offset);
    } else {
      document.documentElement.style.removeProperty('--header-group-height');
      document.body.style.removeProperty('--header-group-height');
    }
  };

  const updateScrolledState = () => {
    scrollFrame = 0;
    instances.forEach((instance, root) => {
      root.classList.toggle('ott-shell-is-scrolled', window.scrollY > 8);
    });
  };

  const requestScrolledState = () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(updateScrolledState);
  };

  const readCartCount = (event) => {
    const detail = event && event.detail;
    const value = detail && detail.cart
      ? detail.cart.item_count
      : detail && detail.item_count;
    const count = Number(value);
    return Number.isFinite(count) ? count : null;
  };

  const updateCartCount = (count) => {
    if (count === null) return;

    document.querySelectorAll('[data-ottuni-cart-count]').forEach((node) => {
      node.textContent = String(count);
    });

    document.querySelectorAll('[data-ottuni-cart-link]').forEach((link) => {
      const label = link.dataset.cartLabel || 'Cart';
      const itemsLabel = link.dataset.itemsLabel || 'items';
      link.setAttribute('aria-label', `${label}, ${count} ${itemsLabel}`);
    });
  };

  const handleCartEvent = (event) => {
    updateCartCount(readCartCount(event));
  };

  const initialize = (root) => {
    if (!root || instances.has(root)) return;

    const drawer = root.querySelector('[data-ottuni-menu]');
    const openButton = root.querySelector('[data-ottuni-menu-open]');
    const closeButton = root.querySelector('[data-ottuni-menu-close]');
    const dropdowns = Array.from(root.querySelectorAll('[data-ottuni-dropdown]'));
    const cleanups = [];
    let restoreFocusOnClose = true;
    let lastFocusedElement = null;

    const addListener = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    const syncDrawerState = () => {
      const isOpen = Boolean(drawer && drawer.open);
      openButton?.setAttribute('aria-expanded', String(isOpen));
      document.documentElement.classList.toggle('ott-shell-menu-is-open', isOpen);
    };

    const closeDrawer = (restoreFocus = true) => {
      if (!drawer || !drawer.open) return;
      restoreFocusOnClose = restoreFocus;

      if (typeof drawer.close === 'function') {
        drawer.close();
      } else {
        drawer.removeAttribute('open');
        syncDrawerState();
        if (restoreFocusOnClose && lastFocusedElement instanceof HTMLElement) {
          lastFocusedElement.focus();
        }
      }
    };

    const openDrawer = () => {
      if (!drawer || drawer.open) return;
      lastFocusedElement = document.activeElement;
      restoreFocusOnClose = true;

      if (typeof drawer.showModal === 'function') {
        drawer.showModal();
      } else {
        drawer.setAttribute('open', '');
      }

      syncDrawerState();
      window.requestAnimationFrame(() => closeButton?.focus());
    };

    const closeDropdowns = (except = null) => {
      dropdowns.forEach((details) => {
        if (details !== except) details.open = false;
      });
    };

    addListener(openButton, 'click', openDrawer);
    addListener(closeButton, 'click', () => closeDrawer(true));
    addListener(drawer, 'click', (event) => {
      if (event.target === drawer) closeDrawer(true);
    });
    addListener(drawer, 'cancel', () => {
      restoreFocusOnClose = true;
    });
    addListener(drawer, 'close', () => {
      syncDrawerState();
      if (restoreFocusOnClose && lastFocusedElement instanceof HTMLElement) {
        lastFocusedElement.focus();
      }
    });

    drawer?.querySelectorAll('a').forEach((link) => {
      addListener(link, 'click', () => closeDrawer(false));
    });

    dropdowns.forEach((details) => {
      addListener(details, 'toggle', () => {
        if (details.open) closeDropdowns(details);
      });
    });

    root.querySelectorAll('[data-ottuni-products-menu]').forEach((details) => {
      const summary = details.querySelector('summary');
      let closeTimer = 0;
      let openedByPointer = false;
      const cancelClose = () => window.clearTimeout(closeTimer);

      addListener(details, 'toggle', () => {
        summary?.setAttribute('aria-expanded', String(details.open));
      });

      if (details.matches('[data-ottuni-dropdown]')) {
        addListener(summary, 'click', (event) => {
          if (event.detail > 0 && openedByPointer && details.open) event.preventDefault();
          openedByPointer = false;
        });
        addListener(details, 'pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !desktopMedia.matches) return;
          cancelClose();
          closeDropdowns(details);
          openedByPointer = !details.open;
          details.open = true;
        });
        addListener(details, 'pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          closeTimer = window.setTimeout(() => {
            if (!details.contains(document.activeElement)) details.open = false;
          }, 150);
        });
        addListener(details, 'focusout', (event) => {
          if (!details.contains(event.relatedTarget)) details.open = false;
        });
      }

      cleanups.push(cancelClose);
    });

    addListener(document, 'pointerdown', (event) => {
      if (!root.contains(event.target)) closeDropdowns();
      else dropdowns.forEach((details) => {
        if (details.matches('[data-ottuni-products-menu]') && !details.contains(event.target)) details.open = false;
      });
    });

    addListener(root, 'keydown', (event) => {
      if (event.key !== 'Escape' || drawer?.open) return;
      const openDropdown = dropdowns.find((details) => details.open);
      if (!openDropdown) return;
      event.preventDefault();
      openDropdown.open = false;
      openDropdown.querySelector('summary')?.focus();
    });

    const handleDesktopChange = (event) => {
      if (event.matches) closeDrawer(false);
      else closeDropdowns();
    };
    addListener(desktopMedia, 'change', handleDesktopChange);

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(updateHeaderOffset)
      : null;
    resizeObserver?.observe(root);
    cleanups.push(() => resizeObserver?.disconnect());

    instances.set(root, {
      destroy: () => {
        closeDrawer(false);
        closeDropdowns();
        cleanups.forEach((cleanup) => cleanup());
        instances.delete(root);
        updateHeaderOffset();
      }
    });

    updateHeaderOffset();
    updateScrolledState();
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.('[data-ottuni-header-shell]')) initialize(scope);
    scope.querySelectorAll?.('[data-ottuni-header-shell]').forEach(initialize);
  };

  const destroyWithin = (scope) => {
    Array.from(instances.entries()).forEach(([root, instance]) => {
      if (root === scope || scope?.contains(root)) instance.destroy();
    });
  };

  window.addEventListener('scroll', requestScrolledState, { passive: true });
  window.addEventListener('cart:update', handleCartEvent);
  window.addEventListener('cart:change', handleCartEvent);
  window.addEventListener('ottuni:cart-change', handleCartEvent);
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:section:reorder', updateHeaderOffset);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  window.OTTUNIGlobalShell = {
    initialize: initializeAll,
    refresh: updateHeaderOffset,
    updateCartCount,
    destroy: destroyWithin
  };
})();
